import { useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { BodyWrapper, Header, Loader, MainWrapper } from '@components';
import { GlobalContext } from '@contexts';
import { groupMutation, groupQuery } from '@queries';
import { ChevronRightIcon } from '@svgs';

import AccountSidebar from '../components/AccountSidebar/AccountSidebar.jsx';
import GroupForm from '../components/GroupForm/GroupForm.jsx';

export const GroupDetails = () => {
  const { t } = useTranslation();
  const { groupID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const globals = useContext(GlobalContext);

  const { data: groupData, isPending: groupIsPending } = useQuery(
    groupQuery(groupID, {
      staleTime: Infinity,
    }),
  );

  const mutation = useMutation(
    groupMutation(groupID, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['groups', 'list'] });
        queryClient.invalidateQueries({
          queryKey: ['groups', 'detail', groupID],
        });
        navigate(`/dashboard/groups`);
      },
    }),
  );

  // exclude access group types from edit UI
  const accessGroupIds = globals.whitelabel.oneoff_access_group_ids
    ? Object.values(globals.whitelabel.oneoff_access_group_ids)
    : [];
  const isAccessGroup = groupData && accessGroupIds.includes(groupData.id);

  return (
    <>
      <Header />
      <BodyWrapper>
        <AccountSidebar />
        <MainWrapper>
          <div className="sb-main-breadcrumbs">
            <ul className="sb-flex">
              <li className="sb-flex">
                <span>Account settings</span>
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                <Link to={`/dashboard/groups`}>{t('Groups')}</Link>
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                <span>{groupData?.name || '...'}</span>
              </li>
            </ul>
          </div>
          <div className="sb-manage-content">
            <h1>Group Details</h1>
            {groupIsPending && <Loader className="sb-loader sb-loader-large" />}
            {groupData && !isAccessGroup && (
              <GroupForm
                groupData={groupData}
                mutation={mutation}
                onCancel={() => navigate('/dashboard/groups')}
                isPending={groupIsPending}
                type="edit" // edit or new
              />
            )}
            {groupData && isAccessGroup && (
              <p>This group cannot be edited in the dashboard.</p>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
